import {MaterialMapsExporterCore, PbrExport} from "@cm/material-maps-exporter"
import {MaterialOutputType, PbrExportEngine, PbrExportFormat, PbrExportNormalY, PbrExportResolution, PbrExportType, PbrExportWorkflow} from "@generated"

/**
 * The exporter names output types by their string values — a shared package must not pull generated
 * code into its dependency graph — while `@generated`'s enum is nominal, so the two need converting
 * at this one boundary. The `satisfies` is what makes the cast safe: if the SDK ever regenerates
 * without one of the six, this stops compiling rather than throwing on a render.
 */
const toMaterialOutputType = (type: PbrExport.Types.PbrExportOutputType): MaterialOutputType =>
    (type satisfies `${MaterialOutputType}`) as MaterialOutputType

/** The same boundary, for the enum the ensure mutation takes. */
const pbrExportTypeForValue = (type: PbrExport.Types.PbrExportOutputType): PbrExportType => (type satisfies `${PbrExportType}`) as PbrExportType

/**
 * The mutation's type argument, from the output type a caller already holds. Both enums carry the
 * same six strings; only the six are convertible, so the guard is what makes the cast safe.
 */
export const toPbrExportType = (type: MaterialOutputType): PbrExportType => {
    if (!PbrExport.Types.isPbrExportMaterialOutputType(type)) throw new Error(`${type} is not a PBR export type`)
    return pbrExportTypeForValue(type)
}

export const getDefaultPbrExportOptions = () =>
    PbrExport.Config.defaultPbrExportOptions().map((option) => ({
        ...option,
        materialOutputType: toMaterialOutputType(option.materialOutputType),
        pbrExportType: pbrExportTypeForValue(option.materialOutputType),
    }))

/**
 * The wire form of an export's settings.
 *
 * Only `normalY` needs translating: GraphQL enum members must match [_a-zA-Z0-9] and `y+up` does
 * not, so the schema names the members and carries the stored values behind them. A `Record` keyed
 * by the vocabulary, so a new member cannot be added without a name on this side too.
 */
const toPbrExportNormalY: Record<MaterialMapsExporterCore.Types.NormalY, PbrExportNormalY> = {
    default: PbrExportNormalY.Default,
    "y+up": PbrExportNormalY.YUp,
    "y+down": PbrExportNormalY.YDown,
}

export const toPbrExportSettingsInput = (settings: PbrExport.Types.Settings) => ({
    workflow: settings.workflow satisfies `${PbrExportWorkflow}` as PbrExportWorkflow,
    engine: settings.engine satisfies `${PbrExportEngine}` as PbrExportEngine,
    normalY: toPbrExportNormalY[settings.normalY],
    format: settings.format satisfies `${PbrExportFormat}` as PbrExportFormat,
    resolution: settings.resolution satisfies `${PbrExportResolution}` as PbrExportResolution,
})
